import React, {useMemo, useState} from "react";
import {IPermission, IPermissionGroup} from "../../../store/permissions/types";
import classnames from "classnames";

interface PermissionsTableProps {
    groups: IPermissionGroup[];
    checkedIds: number[];
    onChangeCheckedIds: (checkedIds: number[]) => void;
}

interface AccordionBodyProps {
    groupId: number;
    permissions: IPermission[];
    checkedIds: number[];
    onChangeCheck: (permissionId: number) => void;
}

interface AccordionBodyItemProps {
    permission: IPermission;
    checkedIds: number[];
    onChangeCheck: (permissionId: number) => void;
}

const AccordionBodyItem: React.FC<AccordionBodyItemProps> = ({permission, checkedIds, onChangeCheck}) => {
    const isChecked = checkedIds.includes(permission.id);

    const handleCheck = () => {
        if (onChangeCheck) onChangeCheck(permission.id);
    }

    return <div className="d-flex align-items-center justify-content-between gap-2 p-2 rounded-2 bg-light-subtle">
        <label htmlFor={`permission-check-${permission.id}`} className="d-flex align-items-center gap-1 text-truncate">
            <div className="d-flex align-items-center gap-2">
                <input id={`permission-check-${permission.id}`} style={{width: "1rem", height: "1rem"}}
                       type="checkbox" checked={isChecked} onChange={handleCheck}/>

                <div className="text-dark fw-bold" role="button">
                    {permission.title}
                </div>
            </div>

            {permission.description && (
                <span style={{display: "contents"}}>
                    <span>–</span>
                    <span className="text-dark text-truncate fw-normal">{permission.description}</span>
                </span>
            )}
        </label>
    </div>
}

const AccordionBody: React.FC<AccordionBodyProps> = ({permissions, checkedIds, onChangeCheck}) => {
    return (
        <div className="px-3 pb-3 pt-1">
            <hr className="my-2 text-secondary opacity-25"/>
            <div className="d-flex flex-column gap-1">
                {permissions.length > 0 ?
                    permissions.map((permission) => (
                        <AccordionBodyItem key={permission.id} permission={permission} checkedIds={checkedIds}
                                           onChangeCheck={onChangeCheck}/>
                    )) : (
                        <span className="my-2 fs-5 text-center fst-italic text-secondary">
                            This permission group is empty!
                        </span>
                    )}
            </div>
        </div>
    )
}

const PermissionsTable: React.FC<PermissionsTableProps> = ({groups, checkedIds, onChangeCheckedIds}) => {
    const [openedGroup, setOpenedGroup] = useState<number | null>(null);

    const toggleAccordion = (id: number): void => setOpenedGroup(openedGroup === id ? null : id);

    const hasSomeSelected = (group: IPermissionGroup) =>
        group.permissions.some(p => checkedIds.includes(p.id));

    const handleCheckboxChange = (permissionId: number) => {
        const updatedChecks = checkedIds.includes(permissionId)
            ? checkedIds.filter(id => permissionId !== id)
            : [...checkedIds, permissionId];

        onChangeCheckedIds(updatedChecks);
    }

    const handleToggleAll = (event: React.MouseEvent<HTMLButtonElement>, groupId: number) => {
        event.stopPropagation();

        const toggleGroup = groups.find(g => g.id === groupId);
        if (!toggleGroup) return;

        const permissionIds = toggleGroup.permissions.map(p => p.id);
        let filteredChecked = checkedIds.filter(id => !permissionIds.includes(id));

        if (!hasSomeSelected(toggleGroup)) filteredChecked = [...filteredChecked, ...permissionIds];

        onChangeCheckedIds(filteredChecked)
    }

    const countChecked = (group: IPermissionGroup) =>
        group.permissions.filter((p) => checkedIds.includes(p.id)).length;

    const filteredGroups = useMemo(() =>
        groups.toSorted((a, b) => Number(!a.permissions.length) - Number(!b.permissions.length)), [groups]);

    return <div className="row align-items-start m-0">
        {filteredGroups.map((group) => {
            const isOpen = openedGroup === group.id;
            const isEmptyPermissions = group.permissions.length === 0;
            const countSelected = countChecked(group);
            const hasSelected = hasSomeSelected(group);

            return (
                <div key={group.id}
                     className={`col-12 col-lg-6 px-2 ${isEmptyPermissions ? "opacity-50" : "opacity-100"}`}>
                    <div className="mb-2 card border-0 shadow-sm rounded-3 overflow-hidden">
                        {/* header */}
                        <button
                            type="button"
                            onClick={() => toggleAccordion(group.id)}
                            className={`btn text-start w-100 border-0 rounded-0 p-2 d-flex align-items-center ${isOpen ? "bg-opacity-10 gap-3" : "bg-light gap-2"}`}
                        >
                            <span
                                className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${isOpen ? "bg-primary text-white" : "bg-light text-secondary"}`}
                                style={{width: "2.5rem", height: "2.5rem"}}>
                                <i className={`bx bx-${group.icon} fs-5`}/>
                            </span>

                            <span className="d-flex gap-2 align-items-center flex-grow-1">
                                <span className="d-block fw-semibold text-dark">{group.name}</span>
                                {!!group.permissions.length &&
                                    <span
                                        className={classnames("badge rounded-pill py-1", {
                                            "bg-primary": !!countSelected,
                                            "text-dark bg-transparent border border-danger": !countSelected
                                        })}>
                                        {countSelected} / {group.permissions.length}
                                    </span>
                                }
                            </span>

                            {!!group.permissions.length &&
                                <button type="button" className={classnames("btn px-2 py-1 font-size-12", {
                                    'btn-outline-success': !hasSelected, 'btn-outline-danger': hasSelected
                                })} onClick={(e) => handleToggleAll(e, group.id)}>
                                    {hasSelected ? "Clear" : "Select"} All
                                </button>}

                            <i className="bx bx-chevron-down fs-4 text-secondary"
                               style={{transform: isOpen ? "rotate(180deg)" : "none"}}/>
                        </button>

                        {/* body */}
                        <div className={isOpen ? "d-block" : "d-none"}>
                            <AccordionBody groupId={group.id} permissions={group.permissions}
                                           checkedIds={checkedIds} onChangeCheck={handleCheckboxChange}/>
                        </div>
                    </div>
                </div>
            );
        })}
    </div>

}

export default PermissionsTable;